import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  OutlinedInput,
  CircularProgress,
  Typography,
  Alert
} from "@mui/material";
import { toast } from "react-toastify";
import {
  fetchInvestorForm,
  submitInvestorForm,
  updateInvestorForm,
  resetState,
} from "../../store/userSlice";
import useAccountInfo from "../../hooks/useAccountInfo";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

const MAX_ANNUAL_INCOME = 1500000; // 1.5 milyon TL

const InvestorForm = () => {
  const dispatch = useDispatch();
  const { loading, error, success, investorInfo } = useSelector((state) => state.user);
  const { accountInfo, loading: accountInfoLoading, error: accountInfoError } = useAccountInfo(); // Custom hook

  // State'ler
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    nationalId: "",
    mkkNo: "",
    annualIncome: "",
  });

  const [isUserApproved, setIsUserApproved] = useState(false);

  const [errors, setErrors] = useState({});

  const [pageLoading, setPageLoading] = useState(true);

  // accountInfo'dan gelen verileri form state'ine aktar
  useEffect(() => {
    if (accountInfo) {
      setFormData(prevData => ({
        ...prevData,
        firstName: accountInfo.firstName || "",
        lastName: accountInfo.lastName || "",
        email: accountInfo.email || "",
        phone: accountInfo.phone || "",
        nationalId: accountInfo.nationalId || "",
      }));
      setIsUserApproved(accountInfo.userApproved || false);
      setPageLoading(false);
    }
  }, [accountInfo]);

  useEffect(() => {
    dispatch(fetchInvestorForm())
      .unwrap()
      .then((response) => {
        if (response) {
          setFormData(prevData => ({
            ...prevData,
            mkkNo: response.mkkNo || "",
            annualIncome: response.annualIncome || "",
          }));
        }
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        setPageLoading(false);
      });
  }, [dispatch]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // annualIncome için özel kontrol
    if (name === 'annualIncome') {
      const numValue = parseFloat(value);
      if (numValue > MAX_ANNUAL_INCOME) {
        toast.error('Yıllık Gelir Beyanınız 1.5 Milyon TL\'den fazla olamaz');
        return;
      }
    }

    const newFormData = {
      ...formData,
      [name]: value,
    };
    setFormData(newFormData);
    validateForm(newFormData);
  };

  const validateForm = (dataToValidate = formData) => {
    let tempErrors = {};
    let isValid = true;

    if (dataToValidate.mkkNo && dataToValidate.mkkNo.trim() === "") {
      tempErrors.mkkNo = "Geçerli bir MKK numarası giriniz";
      isValid = false;
    }

    if (dataToValidate.annualIncome) {
      const income = parseFloat(dataToValidate.annualIncome);
      if (income < 0) {
        tempErrors.annualIncome = "Geçerli bir yıllık gelir giriniz";
        isValid = false;
      } else if (income > MAX_ANNUAL_INCOME) {
        tempErrors.annualIncome = "Yıllık gelir 1.5 Milyon TL'den fazla olamaz";
        isValid = false;
      }
    }

    setErrors(tempErrors);
    return isValid;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Lütfen tüm alanları doldurun ve hataları düzeltin.");
      return;
    }

    const action = investorInfo ? updateInvestorForm : submitInvestorForm;

    dispatch(action(formData))
      .unwrap()
      .then((response) => {
        toast.success("İşlem başarıyla gerçekleştirildi!");
        dispatch(resetState());

        if (response && response.investorInfo) {
          setFormData({
            firstName: response.investorInfo.firstName || "",
            lastName: response.investorInfo.lastName || "",
            email: response.investorInfo.email || "",
            phone: response.investorInfo.phone || "",
            nationalId: response.investorInfo.nationalId || "",
            mkkNo: response.investorInfo.mkkNo || "",
            annualIncome: response.investorInfo.annualIncome || "",
          });
          console.log(response.investorInfo.userApproved);
          setIsUserApproved(response.investorInfo.userApproved || false);
        }
      })
      .catch((err) => {
        toast.error(err || "Form gönderimi başarısız.");
      });
  };

  // Return loading spinner while page is loading
  if (pageLoading) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh' 
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ margin: "0 auto" }}>
      <Alert
        severity="info"
        sx={{ mb: 3 }}
      >
        E-Devlet doğrulaması yapıldıktan sonra Ad Soyad bilgileri değiştirilemez
      </Alert>
      <Grid container spacing={2}>
        {/* Kişisel Bilgiler */}
        <Grid item xs={12} sm={6}>
          <TextField
            name="firstName"
            label="Adınız"
            value={formData.firstName}
            onChange={handleChange}
            fullWidth
            disabled={isUserApproved}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            name="lastName"
            label="Soyadınız"
            value={formData.lastName}
            onChange={handleChange}
            fullWidth
            disabled={isUserApproved}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            name="email"
            label="E-Posta"
            value={formData.email}
            onChange={handleChange}
            fullWidth
            disabled={isUserApproved}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            name="phone"
            label="Telefon"
            value={formData.phone}
            onChange={handleChange}
            fullWidth
            disabled={isUserApproved}
          />
        </Grid>
        {/* TC Kimlik ve E-devlet doğrulama - düzeltilmiş hizalama */}
        <Grid item xs={12}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                name="nationalId"
                label="T.C. Kimlik Numarası"
                value={formData.nationalId}
                onChange={handleChange}
                fullWidth
                disabled={isUserApproved}
              />
            </Grid>
            <Grid item xs={12} sm={6} sx={{ display: 'flex', alignItems: 'center' }}>
              {isUserApproved ? (
                <Alert
                  severity="success"
                  sx={{
                    width: '100%',
                    height: '56px',
                    display: 'flex',
                    alignItems: 'center',
                    backgroundColor: '#e8f5e9'  // Açık yeşil arka plan
                  }}
                >
                  E-Devlet doğrulaması yapıldı
                </Alert>
              ) : (
                <Button
                  variant="contained"
                  sx={{
                    backgroundColor: "#c62828",
                    "&:hover": { backgroundColor: "#b71c1c" },
                    height: '56px',
                    width: '50%'
                  }}
                >
                  E-Devlet ile Doğrula
                </Button>
              )}
            </Grid>
          </Grid>
        </Grid>
        {/* MKK ve Gelir Beyanı yan yana */}
        <Grid item xs={12} sm={6}>
          <TextField
            name="mkkNo"
            label="MKK No (Opsiyonel)"
            value={formData.mkkNo}
            onChange={handleChange}
            fullWidth
            error={!!errors.mkkNo}
            helperText={errors.mkkNo || "Varsa MKK numaranızı giriniz"}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            name="annualIncome"
            label="Gelir Beyanı (Yıllık) (Opsiyonel)"
            value={formData.annualIncome}
            onChange={handleChange}
            fullWidth
            type="number"
            error={!!errors.annualIncome}
            helperText={errors.annualIncome || "Varsa yıllık gelir beyanınızı giriniz (maksimum 1.5 Milyon TL)"}
            inputProps={{
              step: "0.01",
              min: "0",
              max: MAX_ANNUAL_INCOME
            }}
          />
        </Grid>
      </Grid>

      <Box sx={{ marginTop: 4, textAlign: "right" }}>
        <Button
          variant="contained"
          type="submit"
          color="primary"
          disabled={loading || Object.keys(errors).length > 0}
        >
          {loading ? <CircularProgress size={24} /> : "Kaydet"}
        </Button>
      </Box>
    </Box>
  );
};

export default InvestorForm;
